import React, { useState, useEffect, useMemo } from "react";
import { Button, Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalHeader, Nav, NavItem, NavLink, Row, TabContent, TabPane, UncontrolledDropdown } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { fetchBookingsDetailsData } from "../../store/bookings/actions";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import BookingRouteMap from "../../components/Common/BookingRouteMap";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const BookingsDetails = () => {
    
    const { id } = useParams();
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector(state => state.Bookings.details);

    useEffect(() => {
        if (id) {
            dispatch(fetchBookingsDetailsData(id));
        }
    }, [dispatch, id]);

    // Debug: Log booking data when it changes
    useEffect(() => {
        if (data?.data?.booking) {
            console.log('Full booking data:', data.data.booking);
            console.log('Booking type:', data.data.booking.type);
            console.log('Industrial details:', data.data.booking.industrial_details);
            if (data.data.booking.industrial_details) {
                console.log('Material images in industrial_details:', data.data.booking.industrial_details.material_images);
            }
        }
    }, [data]);

    // Custom Tabs Bordered
    const [customActiveTab, setcustomActiveTab] = useState("1");
    const toggleCustom = (tab) => {
        if (customActiveTab !== tab) {
            setcustomActiveTab(tab);
        }
    };

    // Image Modal State
    const [imageModal, setImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const toggleImageModal = () => setImageModal(!imageModal);

    // Cancel ride state
    const [cancelLoading, setCancelLoading] = useState(false);
    const bookingStatus = data?.data?.booking?.status;
    const isCancelled = bookingStatus === 'cancel';
    const isCompleted = bookingStatus === 'complete';
    const canCancelRide = !isCancelled && !isCompleted;
    const handleCancelRide = async () => {
        if (!id || !canCancelRide) return;
        if (!window.confirm("Are you sure you want to cancel this ride?")) return;
        setCancelLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            await axios.post(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/bookings/${id}/cancel`,
                { reason: "Cancelled from portal" },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            dispatch(fetchBookingsDetailsData(id));
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Failed to cancel";
            window.alert(msg);
        } finally {
            setCancelLoading(false);
        }
    };
    const openImageModal = (imageUrl) => {
        console.log('Opening image modal with URL:', imageUrl);
        if (imageUrl) {
            setSelectedImage(imageUrl);
            setImageModal(true);
        } else {
            console.error('No image URL provided');
        }
    };

    const statusMap = {
        pending: 'PENDING',
        ongoing: 'ONGOING',
        arrived: 'ARRIVED',
        start: 'START',
        complete: 'COMPLETE',
        cancel: 'CANCELLED',
        expired: 'EXPIRED',
        scheduled: 'SCHEDULED',
        scheduled_accept: 'SCHEDULE ACCEPT',
    };

    const bookingTracking = {
        "booking_created": {
            "name": "Booking Created",
            "icon": "mdi mdi-calendar-plus",
            "class": "info",
        },
        "ongoing": {
            "name": "Booking Accepted",
            "icon": "mdi mdi-account-check-outline",
            "class": "warning",
        },
        "arrived": {
            "name": "Booking Arrived",
            "icon": "mdi mdi-map-marker-radius",
            "class": "secondary",
        },
        "hooked": {
            "name": "Hooked",
            "icon": "mdi mdi-car-outline",
            "class": "success",
        },
        "loading_proccess": {
            "name": "Loading Proccess",
            "icon": "mdi mdi-calendar-plus",
            "class": "purple",
        },
        "start": {
            "name": "Booking Started",
            "icon": "mdi mdi-car-outline",
            "class": "success",
        },
        "unloading_proccess": {
            "name": "Un-loading Proccess",
            "icon": "mdi mdi-calendar-plus",
            "class": "warning",
        },
        "complete": {
            "name": "Booking Complete",
            "icon": "mdi mdi-flag-checkered",
            "class": "dark",
        },
        "payment_collect": {
            "name": "Payment Collected",
            "icon": "mdi mdi-cash-multiple",
            "class": "success",
        },
    };

    document.title = "Bookings Details | Moeen";
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    {/* Render Breadcrumbs */}
                    <Breadcrumbs maintitle="Home" title="Bookings" breadcrumbItem="Bookings Details" />

                    <Row>
                        <Col xl={3}>
                            <div className="user-sidebar">
                                {/* Booking Status */}
                                <Card className="text-white" style={{ backgroundColor: '#4caf50', border: 'none' }}>
                                    <div className="p-4">
                                        <h5 className="mb-0 text-center">
                                            <span className="text-uppercase">
                                                {console.log(data?.data?.booking?.status)}
                                                {loading ? (
                                                    <Skeleton width={80} />
                                                ) : (
                                                    statusMap[data?.data?.booking?.status] || 'PENDING'
                                                )}
                                            </span>
                                        </h5>
                                    </div>
                                </Card>

                                {/* Customer Card */}
                                <Card className="position-relative">
                                    <div
                                        className="position-absolute bg-t-success text-white px-3 py-1"
                                        style={{
                                            top: "10px",
                                            right: "-10px",
                                            borderTopLeftRadius: "0",
                                            borderBottomLeftRadius: "0",
                                            borderTopRightRadius: "8px",
                                            borderBottomRightRadius: "8px",
                                            zIndex: 1,
                                        }}
                                    >
                                        Customer
                                    </div>
                                    <CardBody>
                                        <div className="mt-n4 position-relative">
                                            <div className="text-center">
                                                {loading ? (
                                                    <Skeleton circle height={80} width={80} />
                                                ) : (
                                                    <img
                                                        src={data?.data?.customer?.image}
                                                        alt={data?.data?.customer?.name}
                                                        className="avatar-xl rounded-circle img-thumbnail"
                                                    />
                                                )}
                                                <div className="mt-3">
                                                    <h5>{loading ? <Skeleton width={100} /> : data?.data?.customer?.name}</h5>
                                                    <div>
                                                        {loading ? (
                                                            <Skeleton width={140} />
                                                        ) : (
                                                            <Link to="#" className="text-muted m-1">
                                                                {data?.data?.customer?.phone_code} {data?.data?.customer?.mobno}
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* Booking for someone else - contact info when contact_id is set */}
                                {data?.data?.contact && (
                                    <Card className="position-relative">
                                        <div
                                            className="position-absolute bg-t-success text-white px-3 py-1"
                                            style={{
                                                top: "10px",
                                                right: "-10px",
                                                borderTopLeftRadius: "0",
                                                borderBottomLeftRadius: "0",
                                                borderTopRightRadius: "8px",
                                                borderBottomRightRadius: "8px",
                                                zIndex: 1,
                                            }}
                                        >
                                            Booking for someone else
                                        </div>
                                        <CardBody>
                                            <div className="mt-n4 position-relative">
                                                <div className="text-center">
                                                    <div className="mt-3">
                                                        <br />
                                                        <h5>{loading ? <Skeleton width={100} /> : data?.data?.contact?.name}</h5>
                                                        <div>
                                                            {loading ? (
                                                                <Skeleton width={140} />
                                                            ) : (
                                                                <Link to="#" className="text-muted m-1">
                                                                    {data?.data?.booking?.contact_phone || data?.data?.contact?.phone_number}
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Driver Card */}
                                <Card className="position-relative">
                                    <div
                                        className="position-absolute bg-t-success text-white px-3 py-1"
                                        style={{
                                            top: "10px",
                                            right: "-10px",
                                            borderTopLeftRadius: "0",
                                            borderBottomLeftRadius: "0",
                                            borderTopRightRadius: "8px",
                                            borderBottomRightRadius: "8px",
                                            zIndex: 1,
                                        }}
                                    >
                                        Driver
                                    </div>
                                    <CardBody>
                                        <div className="mt-n4 position-relative">
                                            <div className="text-center">
                                                {loading ? (
                                                    <Skeleton circle height={80} width={80} />
                                                ) : (
                                                    <img
                                                        src={data?.data?.driver?.image}
                                                        alt={data?.data?.driver?.name}
                                                        className="avatar-xl rounded-circle img-thumbnail"
                                                    />
                                                )}
                                                <div className="mt-3">
                                                    <h5>{loading ? <Skeleton width={100} /> : data?.data?.driver?.name}</h5>
                                                    <div>
                                                        {loading ? (
                                                            <Skeleton width={140} />
                                                        ) : (
                                                            <Link to="#" className="text-muted m-1">
                                                                {data?.data?.driver?.phone_code} {data?.data?.driver?.mobno}
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>

                                {/* Vehicle Card */}
                                <Card className="position-relative">
                                    <div
                                        className="position-absolute bg-t-success text-white px-3 py-1"
                                        style={{
                                            top: "10px",
                                            right: "-10px",
                                            borderTopLeftRadius: "0",
                                            borderBottomLeftRadius: "0",
                                            borderTopRightRadius: "8px",
                                            borderBottomRightRadius: "8px",
                                            zIndex: 1,
                                        }}
                                    >
                                        Vehicle
                                    </div>
                                    <CardBody>
                                        <div className="mt-n4 position-relative">
                                            <div className="text-center">
                                                {loading ? (
                                                    <Skeleton circle height={80} width={80} />
                                                ) : (
                                                    <img
                                                        src={data?.data?.vehicle?.vehicle_media?.[0]?.url}
                                                        alt={data?.data?.vehicle?.make_name}
                                                        className="avatar-xl rounded-circle img-thumbnail"
                                                    />
                                                )}
                                                <div className="mt-3">
                                                    <h5>
                                                        {loading ? (
                                                            <Skeleton width={120} />
                                                        ) : (
                                                            `${data?.data?.vehicle?.make_name} - ${data?.data?.vehicle?.body_type}`
                                                        )}
                                                    </h5>
                                                    <div>
                                                        {loading ? (
                                                            <Skeleton width={160} />
                                                        ) : (
                                                            <Link to="#" className="text-muted m-1">
                                                                {data?.data?.vehicle?.license_plate} - {data?.data?.vehicle?.type_name}
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </Col>

                        <Col xl={9}>

                            <Card>
                                <Nav tabs className="nav nav-tabs nav-tabs-custom nav-justified">
                                    <NavItem>
                                        <NavLink
                                            style={{ cursor: "pointer" }}
                                            className={classnames({
                                                active: customActiveTab === "1",
                                            })}
                                            onClick={() => {
                                                toggleCustom("1");
                                            }}
                                        >
                                            <i className="bx bx-user-circle font-size-20"></i>
                                            <span className="d-none d-sm-block">Location</span>
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            style={{ cursor: "pointer" }}
                                            className={classnames({
                                                active: customActiveTab === "2",
                                            })}
                                            onClick={() => {
                                                toggleCustom("2");
                                            }}
                                        >
                                            <i className="bx bx-clipboard font-size-20"></i>
                                            <span className="d-none d-sm-block">Tracking</span>
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            style={{ cursor: "pointer" }}
                                            className={classnames({
                                                active: customActiveTab === "3",
                                            })}
                                            onClick={() => {
                                                toggleCustom("3");
                                            }}
                                        >
                                            <i className="bx bx-mail-send font-size-20"></i>
                                            <span className="d-none d-sm-block">Fares</span>
                                        </NavLink>
                                    </NavItem>
                                    {data?.data?.booking?.type === "industrial" && 
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({
                                                    active: customActiveTab === "4",
                                                })}
                                                onClick={() => {
                                                    toggleCustom("4");
                                                }}
                                            >
                                                <i className="bx bx-user-circle font-size-20"></i>
                                                <span className="d-none d-sm-block">Industrial details</span>
                                            </NavLink>
                                        </NavItem>
                                    }
                                    {data?.data?.booking?.type === "waste_pickup" && 
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({
                                                    active: customActiveTab === "5",
                                                })}
                                                onClick={() => {
                                                    toggleCustom("5");
                                                }}
                                            >
                                                <i className="bx bx-user-circle font-size-20"></i>
                                                <span className="d-none d-sm-block">Waste pickup details</span>
                                            </NavLink>
                                        </NavItem>
                                    }
                                    {data?.data?.booking?.type === "towing" && 
                                        <NavItem>
                                            <NavLink
                                                style={{ cursor: "pointer" }}
                                                className={classnames({
                                                    active: customActiveTab === "6",
                                                })}
                                                onClick={() => {
                                                    toggleCustom("6");
                                                }}
                                            >
                                                <i className="bx bx-user-circle font-size-20"></i>
                                                <span className="d-none d-sm-block">Industrial details</span>
                                            </NavLink>
                                        </NavItem>
                                    }
                                </Nav>

                                <TabContent
                                    activeTab={customActiveTab}
                                    className="p-4"
                                >
                                    <TabPane tabId="1">

                                        <div>
                                            <div>
                                                <h5 className="font-size-16 mb-4">Locations</h5>

                                                <ol className="activity-checkout mb-0 px-4 mt-3">
                                                    <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-primary">
                                                                <i className="mdi mdi-map-marker text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">
                                                            <div>
                                                                <h5 className="font-size-16 mb-1">{loading ? <Skeleton width={200} /> : data?.data?.booking?.pickup_addr}</h5>
                                                                {loading ? (
                                                                    <Skeleton width={150} />
                                                                ) : data?.data?.booking?.tracking?.[1]?.datetime && (
                                                                    <p className="text-muted text-truncate mb-2">
                                                                        {data.data.booking.tracking[1].datetime}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </li>

                                                    <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-primary">
                                                                <i className="mdi mdi-map-marker-check text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">

                                                            <h5 className="font-size-16 mb-1">{loading ? <Skeleton width={200} /> : data?.data?.booking?.dest_addr}</h5>
                                                            {loading ? (
                                                                <Skeleton width={150} />
                                                            ) : data?.data?.booking?.tracking?.[5]?.datetime && (
                                                                <p className="text-muted text-truncate mb-2">
                                                                    {data.data.booking.tracking[5].datetime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>

                                                </ol>
                                            </div>

                                            <div>
                                                <h5 className="font-size-16 mb-4">Map</h5>
                                                {loading ? (
                                                    <Skeleton height={300} />
                                                ) : (
                                                    <BookingRouteMap
                                                        src_lat={data?.data?.booking?.source_lat}
                                                        src_lng={data?.data?.booking?.source_long}
                                                        dest_lat={data?.data?.booking?.dest_lat}
                                                        dest_lng={data?.data?.booking?.dest_long}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                    </TabPane>
                                    <TabPane tabId="2">

                                        <div>
                                            <div>
                                                <h5 className="font-size-16 mb-4">Booking Tracking</h5>

                                                <ol className="activity-checkout mb-0 px-4 mt-3">

                                                    {data?.data?.booking?.tracking?.map((track, index) => {
                                                        const trackInfo = bookingTracking[track.status];
                                                        if (!trackInfo) return null;

                                                        return (
                                                            <li key={index} className="checkout-item">
                                                                <div className="avatar-sm checkout-icon p-1">
                                                                    <div className={`avatar-title rounded-circle bg-${trackInfo.class}`}>
                                                                        <i className={`${trackInfo.icon} text-white font-size-20`}></i>
                                                                    </div>
                                                                </div>
                                                                <div className="feed-item-list">
                                                                    <h5 className="font-size-16 mb-1">{trackInfo.name}</h5>
                                                                    {track.datetime && (
                                                                        <p className="text-muted text-truncate mb-2">{track.datetime}</p>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        );
                                                    })}

                                                    {/* <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-warning">
                                                                <i className="text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">
                                                            <h5 className="font-size-16 mb-1">Booking Accepted</h5>
                                                            {data?.data?.booking?.tracking?.[1]?.datetime && (
                                                                <p className="text-muted text-truncate mb-2">
                                                                    {data.data.booking.tracking[1].datetime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>

                                                    <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-secondary">
                                                                <i className="text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">
                                                            <h5 className="font-size-16 mb-1">Booking Arrived</h5>
                                                            {data?.data?.booking?.tracking?.[2]?.datetime && (
                                                                <p className="text-muted text-truncate mb-2">
                                                                    {data.data.booking.tracking[2].datetime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>

                                                    <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-success">
                                                                <i className="text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">
                                                            <h5 className="font-size-16 mb-1">Booking Started</h5>
                                                            {data?.data?.booking?.tracking?.[3]?.datetime && (
                                                                <p className="text-muted text-truncate mb-2">
                                                                    {data.data.booking.tracking[3].datetime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>

                                                    <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-dark">
                                                                <i className="text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">
                                                            <h5 className="font-size-16 mb-1">Booking Complete</h5>
                                                            {data?.data?.booking?.tracking?.[4]?.datetime && (
                                                                <p className="text-muted text-truncate mb-2">
                                                                    {data.data.booking.tracking[4].datetime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li>

                                                    <li className="checkout-item">
                                                        <div className="avatar-sm checkout-icon p-1">
                                                            <div className="avatar-title rounded-circle bg-success">
                                                                <i className="text-white font-size-20"></i>
                                                            </div>
                                                        </div>
                                                        <div className="feed-item-list">
                                                            <h5 className="font-size-16 mb-1">Payment Collected</h5>
                                                            {data?.data?.booking?.tracking?.[5]?.datetime && (
                                                                <p className="text-muted text-truncate mb-2">
                                                                    {data.data.booking.tracking[5].datetime}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </li> */}
                                                </ol>

                                            </div>
                                            <div className="d-flex justify-content-end mt-4">
                                                <Button
                                                    color="danger"
                                                    onClick={handleCancelRide}
                                                    disabled={!canCancelRide || cancelLoading || loading}
                                                >
                                                    {cancelLoading ? "Cancelling..." : "Cancel ride"}
                                                </Button>
                                            </div>
                                        </div>

                                    </TabPane>
                                    <TabPane tabId="3">

                                        <div>
                                            <h5 className="font-size-16 mb-4">Fare Summary</h5>
                                            <div className="table-responsive">
                                                <table className="table table-bordered mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <th>Pickup Location</th>
                                                            <td>{data?.data?.booking?.pickup_addr}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Drop-off Location</th>
                                                            <td>{data?.data?.booking?.dest_addr}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Booking Type</th>
                                                            <td className="text-capitalize">{data?.data?.booking?.type}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Distance</th>
                                                            <td>{data?.data?.booking?.total_kms} km</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Base Fare</th>
                                                            <td>Rs. {data?.data?.booking?.base_fare?.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Standard Fare (Distance)</th>
                                                            <td>Rs. {data?.data?.booking?.std_fare?.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Time Fare</th>
                                                            <td>Rs. {data?.data?.booking?.time_rate?.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Other Charges</th>
                                                            <td>Rs. {data?.data?.booking?.other_fee?.toFixed(2)}</td>
                                                        </tr>
                                                        <tr className="table-active font-weight-bold">
                                                            <th>Total Fare</th>
                                                            <td>Rs. {data?.data?.booking?.total_amount?.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Payment Method</th>
                                                            <td className="text-capitalize">{data?.data?.booking?.payment_method}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Payment Status</th>
                                                            <td className="text-capitalize">{data?.data?.booking?.payment_status == 1 ? 'PAID' : 'NOT PAID'}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                    </TabPane>
                                    <TabPane tabId="4">
                                        <div>
                                            <h5 className="font-size-16 mb-3 fw-bold">Industrial Shipment Details</h5>
                                            <div className="p-3 border rounded">
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <div className="fw-bold">Main Item</div>
                                                    <div>{data?.data?.booking?.industrial_details?.main_type_of_items}</div>
                                                    <div className="fw-bold mt-3">Material</div>
                                                    <div>{data?.data?.booking?.industrial_details?.material_name}</div>
                                                    <Row className="mb-3 mt-3">
                                                        <Col md={6}>
                                                            <div className="fw-bold">Weight</div>
                                                            <div>{data?.data?.booking?.industrial_details?.weight} kg</div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="fw-bold">Number of Items</div>
                                                            <div>{data?.data?.booking?.industrial_details?.number_of_items}</div>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <div className="fw-bold">Pickup Floor</div>
                                                            <div>{data?.data?.booking?.industrial_details?.pickup_floor}</div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="fw-bold">Elevator Available</div>
                                                            <div>{data?.data?.booking?.industrial_details?.is_elevator ? "Yes" : "No"}</div>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="fw-bold text-primary">Material Images</div>
                                                    <div>
                                                        {(() => {
                                                            // Try multiple possible paths for material images
                                                            const industrialDetails = data?.data?.booking?.industrial_details;
                                                            console.log('Full industrial_details object:', industrialDetails);
                                                            
                                                            // Try different possible paths
                                                            let materialImages = industrialDetails?.material_images;
                                                            
                                                            // If material_images is undefined, try alternative paths
                                                            if (!materialImages && industrialDetails) {
                                                                console.log('material_images not found, checking alternative paths...');
                                                                console.log('All keys in industrial_details:', Object.keys(industrialDetails || {}));
                                                                
                                                                // Try other possible property names
                                                                materialImages = industrialDetails?.images || 
                                                                                 industrialDetails?.materialMedia || 
                                                                                 industrialDetails?.media;
                                                            }
                                                            
                                                            console.log('Final Material Images Data:', materialImages);
                                                            console.log('Material Images Type:', typeof materialImages);
                                                            console.log('Material Images Length:', materialImages?.length);
                                                            
                                                            // Handle array
                                                            if (Array.isArray(materialImages) && materialImages.length > 0) {
                                                                return materialImages.map((img, i) => {
                                                                    console.log(`Image ${i}:`, img);
                                                                    // Handle both object format {id, url} and string URL
                                                                    const imageUrl = typeof img === 'string' ? img : (img?.url || img?.src || img?.path);
                                                                    const imageId = img?.id || i;
                                                                    
                                                                    if (!imageUrl) {
                                                                        console.warn(`Image ${i} has no valid URL:`, img);
                                                                        return null;
                                                                    }
                                                                    
                                                                    return (
                                                                        <img
                                                                        key={imageId}
                                                                        src={imageUrl}
                                                                        alt={`Material ${i + 1}`}
                                                                        className="img-thumbnail me-2 mt-2 material-image-clickable"
                                                                        style={{ 
                                                                            width: "100px", 
                                                                            height: "100px", 
                                                                            objectFit: "cover", 
                                                                            cursor: "pointer",
                                                                            transition: "opacity 0.2s"
                                                                        }}
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            console.log('Image clicked - Full img object:', img);
                                                                            console.log('Image clicked - URL:', imageUrl);
                                                                            if (imageUrl) {
                                                                                openImageModal(imageUrl);
                                                                            } else {
                                                                                console.error('No image URL available for image:', img);
                                                                            }
                                                                        }}
                                                                        onMouseEnter={(e) => {
                                                                            e.currentTarget.style.opacity = "0.8";
                                                                        }}
                                                                        onMouseLeave={(e) => {
                                                                            e.currentTarget.style.opacity = "1";
                                                                        }}
                                                                        onError={(e) => {
                                                                            console.error('Image load error:', imageUrl);
                                                                        }}
                                                                        />
                                                                    );
                                                                }).filter(Boolean);
                                                            } else if (materialImages === undefined || materialImages === null) {
                                                                console.warn('material_images is undefined or null');
                                                                return <span className="text-muted">No images data available</span>;
                                                            } else {
                                                                console.warn('material_images is not an array or is empty:', materialImages);
                                                                return <span>No Images</span>;
                                                            }
                                                        })()}
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="bg-light p-3 rounded">
                                                <Col md={4}>
                                                <div className="fw-bold">Workers Required</div>
                                                <div>{data?.data?.booking?.industrial_details?.is_worker_required ? "Yes" : "No"}</div>
                                                </Col>
                                                <Col md={4}>
                                                <div className="fw-bold">Disassembled</div>
                                                <div>{data?.data?.booking?.industrial_details?.items_disassembled ? "Yes" : "No"}</div>
                                                </Col>
                                                <Col md={4}>
                                                <div className="fw-bold">Number of Workers</div>
                                                <div>{data?.data?.booking?.industrial_details?.number_of_worker}</div>
                                                </Col>
                                            </Row>

                                            </div>
                                        </div>
                                    </TabPane>
                                    <TabPane tabId="5">
                                    <div>
                                        <h5 className="font-size-16 mb-3 fw-bold">Waste Pickup Details</h5>
                                        <div className="p-3 border rounded">

                                        <Row className="mb-3">
                                            <Col md={6}>
                                            <div className="fw-bold">Material</div>
                                            <div>{data?.data?.booking?.waste_pickup_details?.material_name || "N/A"}</div>

                                            <div className="fw-bold mt-3">Weight</div>

                                            <div>{data?.data?.booking?.waste_pickup_details?.weight} kg</div>
                                            <Row className="mb-3 mt-3">
                                                <Col md={6}>
                                                <div className="fw-bold">Needs Assistance</div>
                                                <div>{data?.data?.booking?.waste_pickup_details?.needs_assistance ? "Yes" : "No"}</div>
                                                </Col>
                                                <Col md={6}>
                                                
                                                </Col>
                                            </Row>
                                            </Col>

                                            <Col md={6}>
                                            <div className="fw-bold text-primary">Material Images</div>
                                            <div>
                                                {data?.data?.booking?.waste_pickup_details?.material_images?.length > 0 ? (
                                                data?.data?.booking?.waste_pickup_details?.material_images.map((img, i) => (
                                                    <img
                                                    key={img.id || i}
                                                    src={img.url}
                                                    alt={`Material ${i + 1}`}
                                                    className="img-thumbnail me-2 mt-2"
                                                    style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                                    />
                                                ))
                                                ) : (
                                                <span>No Images</span>
                                                )}
                                            </div>
                                            </Col>
                                        </Row>

                                        <Row className="bg-light p-3 rounded">
                                            <Col md={4}>
                                            <div className="fw-bold">Price per Worker</div>
                                            <div>{data?.data?.booking?.waste_pickup_details?.price_per_worker}</div>
                                            </Col>
                                            <Col md={4}>
                                            <div className="fw-bold">Workers Count</div>
                                            <div>{data?.data?.booking?.waste_pickup_details?.workers_count}</div>
                                            </Col>
                                            <Col md={4}>
                                            <div className="fw-bold">Total Worker Cost</div>
                                            <div>
                                                Rs.{" "}
                                                {(
                                                data?.data?.booking?.waste_pickup_details?.workers_count *
                                                parseFloat(data?.data?.booking?.waste_pickup_details?.price_per_worker || 0)
                                                ).toFixed(2)}
                                            </div>
                                            </Col>
                                        </Row>

                                        </div>
                                    </div>
                                    </TabPane>
                                    <TabPane tabId="6">
                                        <div>
                                            <h5 className="font-size-16 mb-3 fw-bold">Towing Details</h5>
                                            <div className="p-3 border rounded">
                                                <Row className="mb-3">
                                                    <Col md={12}>
                                                        <div className="fw-bold text-primary">Material Images</div>
                                                        <div>
                                                            {(() => {
                                                                const twingDetails = data?.data?.booking?.twing_details;
                                                                const materialImages = twingDetails?.material_images;
                                                                
                                                                console.log('Towing - Full twing_details object:', twingDetails);
                                                                console.log('Towing - Material Images Data:', materialImages);
                                                                console.log('Towing - Material Images Length:', materialImages?.length);
                                                                
                                                                if (Array.isArray(materialImages) && materialImages.length > 0) {
                                                                    return materialImages.map((img, i) => {
                                                                        console.log(`Towing - Image ${i}:`, img);
                                                                        const imageUrl = typeof img === 'string' ? img : (img?.url || img?.src || img?.path);
                                                                        const imageId = img?.id || i;
                                                                        
                                                                        if (!imageUrl) {
                                                                            console.warn(`Towing - Image ${i} has no valid URL:`, img);
                                                                            return null;
                                                                        }
                                                                        
                                                                        return (
                                                                            <img
                                                                            key={imageId}
                                                                            src={imageUrl}
                                                                            alt={`Material ${i + 1}`}
                                                                            className="img-thumbnail me-2 mt-2 material-image-clickable"
                                                                            style={{ 
                                                                                width: "100px", 
                                                                                height: "100px", 
                                                                                objectFit: "cover", 
                                                                                cursor: "pointer",
                                                                                transition: "opacity 0.2s"
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                console.log('Towing - Image clicked - Full img object:', img);
                                                                                console.log('Towing - Image clicked - URL:', imageUrl);
                                                                                if (imageUrl) {
                                                                                    openImageModal(imageUrl);
                                                                                } else {
                                                                                    console.error('Towing - No image URL available for image:', img);
                                                                                }
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.opacity = "0.8";
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.opacity = "1";
                                                                            }}
                                                                            onError={(e) => {
                                                                                console.error('Towing - Image load error:', imageUrl);
                                                                            }}
                                                                            />
                                                                        );
                                                                    }).filter(Boolean);
                                                                } else if (materialImages === undefined || materialImages === null) {
                                                                    console.warn('Towing - material_images is undefined or null');
                                                                    return <span className="text-muted">No images data available</span>;
                                                                } else {
                                                                    console.warn('Towing - material_images is not an array or is empty:', materialImages);
                                                                    return <span>No Images</span>;
                                                                }
                                                            })()}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </TabPane>

                                </TabContent>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Image Modal */}
            <Modal 
                isOpen={imageModal} 
                toggle={toggleImageModal} 
                size="lg" 
                centered
                style={{ zIndex: 1055 }}
            >
                <ModalHeader toggle={toggleImageModal}>
                    Material Image
                </ModalHeader>
                <ModalBody className="p-0" style={{ maxHeight: "80vh", overflow: "auto" }}>
                    {selectedImage ? (
                        <img
                            src={selectedImage}
                            alt="Enlarged material"
                            style={{ width: "100%", height: "auto", display: "block" }}
                            onError={(e) => {
                                console.error('Modal image load error:', selectedImage);
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not found%3C/text%3E%3C/svg%3E';
                            }}
                        />
                    ) : (
                        <div className="p-4 text-center">No image selected</div>
                    )}
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default BookingsDetails;
